import React, { useCallback, useEffect, useState } from 'react';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, BriefcaseBusiness, Calendar, DollarSign, HandCoins, LineChart, Target, TrendingUp, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { crmChartOptions, crmDoughnutOptions, crmPalette } from '@/lib/chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function formatCLP(amount: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(amount || 0);
}

function getPresetDates(preset: string): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  if (preset === '7d') {
    const d = new Date(now); d.setDate(d.getDate() - 7);
    return { from: d.toISOString().split('T')[0], to };
  }
  if (preset === '30d') {
    const d = new Date(now); d.setDate(d.getDate() - 30);
    return { from: d.toISOString().split('T')[0], to };
  }
  if (preset === '90d') {
    const d = new Date(now); d.setDate(d.getDate() - 90);
    return { from: d.toISOString().split('T')[0], to };
  }
  return { from: '', to: '' };
}

export default function DashboardPage() {
  const [kpis, setKpis] = useState<any>(null);
  const [charts, setCharts] = useState<any>(null);
  const [period, setPeriod] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const { currentOrgId } = useOrgStore();
  const navigate = useNavigate();

  const fetchData = useCallback(() => {
    if (!currentOrgId) return;
    const params: any = {};
    if (period === 'custom') {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
    } else if (period !== 'all') {
      const { from, to } = getPresetDates(period);
      params.date_from = from;
      params.date_to = to;
    }
    api.get('/dashboard/kpis', { params }).then((response) => setKpis(response.data)).catch(console.error);
    api.get('/dashboard/charts', { params }).then((response) => setCharts(response.data)).catch(console.error);
  }, [currentOrgId, period, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const kpiCards = kpis ? [
    { label: 'Leads', value: kpis.leads_count, icon: Users, color: '#2563EB', bg: '#EFF6FF', metric: 'leads' },
    { label: 'Prospectos', value: kpis.prospects_count, icon: Target, color: '#8B5CF6', bg: '#F5F3FF', metric: 'leads' },
    { label: 'Clientes', value: kpis.clients_count, icon: BriefcaseBusiness, color: '#0EA5E9', bg: '#ECFEFF', metric: 'won' },
    { label: 'Ventas', value: kpis.sales_count, icon: HandCoins, color: '#22C55E', bg: '#F0FDF4', metric: 'won' },
    { label: 'Conversiones', value: `${kpis.conversion_rate}%`, icon: TrendingUp, color: '#F59E0B', bg: '#FFFBEB', metric: 'conversion' },
    { label: 'Pipeline abierto', value: formatCLP(kpis.pipeline_value), icon: DollarSign, color: '#2563EB', bg: '#EFF6FF', metric: 'pipeline' },
    { label: 'Pipeline total', value: formatCLP(kpis.pipeline_total), icon: DollarSign, color: '#0EA5E9', bg: '#ECFEFF', metric: 'pipeline' },
    { label: 'Pipeline ponderado', value: formatCLP(kpis.weighted_pipeline), icon: LineChart, color: '#7C3AED', bg: '#F5F3FF', metric: 'pipeline' },
    { label: 'Forecast mensual', value: formatCLP(kpis.monthly_forecast), icon: Calendar, color: '#059669', bg: '#ECFDF5', metric: 'pipeline' },
    { label: 'Actividades pendientes', value: kpis.pending_activities, icon: Users, color: '#F97316', bg: '#FFF7ED', metric: 'activities' },
    { label: 'Oportunidades en riesgo', value: kpis.opps_at_risk, icon: AlertTriangle, color: '#DC2626', bg: '#FEF2F2', metric: 'risk' },
    { label: 'Sin próxima actividad', value: kpis.opps_no_next_step, icon: AlertTriangle, color: '#F59E0B', bg: '#FFFBEB', metric: 'no_next' },
    { label: 'Leads sin gestionar (24h)', value: kpis.leads_overdue_24h, icon: AlertTriangle, color: '#F97316', bg: '#FFF7ED', metric: 'leads_overdue' },
    { label: 'Alertas (total)', value: kpis.alerts_count, icon: AlertTriangle, color: '#DC2626', bg: '#FEF2F2', metric: 'inactivity' },
  ] : [];

  const stageOrder = ['Prospecto', 'Contacto realizado', 'Reunion / demo', 'Propuesta enviada', 'Negociacion'];
  const pipelineChartData = charts?.pipeline_by_stage ? {
    labels: stageOrder.filter((stage) => charts.pipeline_by_stage.find((item: any) => item._id === stage)),
    datasets: [{
      label: 'Monto (CLP)',
      data: stageOrder
        .map((stage) => {
          const found = charts.pipeline_by_stage.find((item: any) => item._id === stage);
          return found ? found.total : 0;
        })
        .filter((_, index) => charts.pipeline_by_stage.find((item: any) => item._id === stageOrder[index])),
      backgroundColor: [crmPalette.slate, crmPalette.blue, crmPalette.teal, crmPalette.amber, crmPalette.violet],
      borderRadius: 8,
      borderSkipped: false,
    }],
  } : null;

  const sourceChartData = charts?.leads_by_source ? {
    labels: charts.leads_by_source.map((item: any) => item._id),
    datasets: [{
      data: charts.leads_by_source.map((item: any) => item.count),
      backgroundColor: ['#2563EB', '#22C55E', '#F59E0B', '#8B5CF6', '#EF4444', '#0EA5E9'],
      borderWidth: 0,
    }],
  } : null;

  const sellerChartData = charts?.seller_performance ? {
    labels: charts.seller_performance.map((item: any) => item._id),
    datasets: [
      {
        label: 'Monto vendido',
        data: charts.seller_performance.map((item: any) => item.amount_sold),
        backgroundColor: '#2563EB',
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Oportunidades',
        data: charts.seller_performance.map((item: any) => item.opportunities_created),
        backgroundColor: '#8B5CF6',
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  } : null;

  const periods = [
    { key: 'all', label: 'Todo' },
    { key: '7d', label: '7 dias' },
    { key: '30d', label: 'Mensual' },
    { key: '90d', label: '90 dias' },
    { key: 'custom', label: 'Personalizado' },
  ];

  const openMetric = (metric: string) => {
    const params = new URLSearchParams();
    if (period === 'custom') {
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
    } else if (period !== 'all') {
      const { from, to } = getPresetDates(period);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
    }
    navigate(`/dashboard/${metric}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Dashboard</h1>
          <p className="text-sm text-[#475569] mt-1">Lectura ejecutiva de pipeline, forecast, conversiones y performance comercial.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="date-filter">
          {periods.map((p) => (
            <Button
              key={p.key}
              variant={period === p.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p.key)}
              className={`text-xs h-8 ${period === p.key ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569]'}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {period === 'custom' && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-white border border-[#E2E8F0] rounded-lg" data-testid="custom-date-range">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Desde</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-40 border-[#E2E8F0] text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Hasta</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-40 border-[#E2E8F0] text-sm" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 mb-6">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.label}
              className="border-[#E2E8F0] shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 cursor-pointer bg-white/95 backdrop-blur overflow-hidden"
              onClick={() => openMetric(card.metric)}
            >
              <div className="h-1 bg-gradient-to-r" style={{ backgroundImage: `linear-gradient(90deg, ${card.color}, ${card.bg})` }} />
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-start sm:items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] mb-1 truncate">{card.label}</p>
                    <p className="text-base sm:text-2xl font-bold tabular-nums text-[#0F172A] truncate" title={String(card.value)}>{card.value}</p>
                  </div>
                  <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center shadow-sm shrink-0" style={{ backgroundColor: card.bg }}>
                    <Icon size={18} className="sm:hidden" style={{ color: card.color }} />
                    <Icon size={22} className="hidden sm:block" style={{ color: card.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#2563EB] via-[#8B5CF6] to-[#22C55E]" />
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Pipeline por etapa</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              {pipelineChartData ? <Bar data={pipelineChartData} options={{ ...crmChartOptions, plugins: { ...crmChartOptions.plugins, legend: { display: false } } }} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Cargando...</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#8B5CF6] via-[#F59E0B] to-[#2563EB]" />
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Leads por fuente</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              {sourceChartData ? <Doughnut data={sourceChartData} options={crmDoughnutOptions} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Cargando...</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="xl:col-span-2 border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#0EA5E9] via-[#2563EB] to-[#8B5CF6]" />
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Performance por vendedor</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              {sellerChartData ? <Bar data={sellerChartData} options={crmChartOptions} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Cargando...</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#DC2626] via-[#F59E0B] to-[#8B5CF6]" />
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Control y alertas</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm text-[#475569]">
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Oportunidades estancadas</p>
              <p className="mt-2 text-2xl font-semibold text-[#0F172A]">{kpis?.stale_opportunities || 0}</p>
            </div>
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Seguimientos vencidos</p>
              <p className="mt-2 text-2xl font-semibold text-[#0F172A]">{kpis?.overdue_followups || 0}</p>
            </div>
            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Negocios perdidos</p>
              <p className="mt-2 text-2xl font-semibold text-[#0F172A]">{kpis?.lost_deals_count || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
